/**
 * ViNCi Game Engine
 * Pure logic — completely independent of UI
 * Handles: board state, move validation, formation detection, scoring, balancing phase, AI
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type PositionId =
  | 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'L6' | 'L7' | 'L8' |'S9' |'R1' | 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'R7' | 'R8';

export type FormationType = 'triangle' | 'line' | 'square';

export type GamePhase =
  | 'setup' |'rolling' |'playing' |'balancing' |'gameover';

export type AIDifficulty = 'easy' | 'medium' | 'hard';

export interface Player {
  id: string;
  name: string;
  color: string;
  colorVar: string;
  isAI: boolean;
  aiDifficulty?: AIDifficulty;
  piecesRemaining: number;
  piecesTotal: number;
  score: number;
  formations: DetectedFormation[];
  isProtected?: boolean; // for balancing phase
  diceRoll?: number;
}

export interface BoardPosition {
  id: PositionId;
  occupiedBy: string | null; // player id or null
  isSpot9: boolean;
  gridLeft: boolean;
  gridRight: boolean;
  displayLabel: string;
  gridRow: number; // 0-2
  gridCol: number; // 0-2
  side: 'left' | 'right' | 'shared';
}

export interface DetectedFormation {
  id: string;
  type: FormationType;
  positions: PositionId[];
  playerId: string;
  points: number;
  turnDetected: number;
}

export interface MoveRecord {
  id: string;
  turnNumber: number;
  playerId: string;
  playerName: string;
  position: PositionId;
  formationsCreated: DetectedFormation[];
  pointsEarned: number;
  timestamp: number;
}

export interface BalancingAction {
  type: 'protect' | 'remove';
  playerId: string;
  targetPlayerId?: string;
  position?: PositionId;
}

export interface GameRules {
  balancingPhase: boolean;
  texasTieBreak: boolean;
  sweetwaterRule: boolean;
  chunnelRule: boolean;
}

export interface GameState {
  id: string;
  phase: GamePhase;
  players: Player[];
  currentPlayerIndex: number;
  board: Record<PositionId, BoardPosition>;
  allFormations: DetectedFormation[];
  moveHistory: MoveRecord[];
  turnNumber: number;
  rules: GameRules;
  balancingState?: BalancingState;
  winner?: Player | null;
  tiedPlayers?: string[]; // ids of tied dice rollers
  lastMoveFormations?: DetectedFormation[]; // formations from last move for animation
}

export interface BalancingState {
  currentActionIndex: number;
  actions: BalancingAction[];
  protectedPositions: PositionId[];
  phase: 'protecting' | 'removing' | 'complete';
}

// ─── Board Graph (Adjacency) ─────────────────────────────────────────────────

/**
 * The ViNCi board consists of two overlapping 3x3 grids sharing Spot 9 (S9).
 * Left Grid (counter-clockwise spiral): 5 4 3 / 6 1 2 / 7 8 9
 *   Positions: L1(center), L2-L8(outer), S9(bottom-right of left grid)
 * Right Grid (counter-clockwise spiral): 9 8 7 / 2 1 6 / 3 4 5
 *   Positions: R1(center), R2-R8(outer), S9(top-left of right grid)
 *
 * Adjacency is based on the grid layout — positions adjacent if they share an edge or diagonal
 */

// Left grid positions by [row][col] (0-indexed)
// Row 0: L5, L4, L3
// Row 1: L6, L1, L2
// Row 2: L7, L8, S9
const LEFT_GRID: PositionId[][] = [
  ['L5', 'L4', 'L3'],
  ['L6', 'L1', 'L2'],
  ['L7', 'L8', 'S9'],
];

// Right grid positions by [row][col] (0-indexed)
// Row 0: S9, R8, R7
// Row 1: R2, R1, R6
// Row 2: R3, R4, R5
const RIGHT_GRID: PositionId[][] = [
  ['S9', 'R8', 'R7'],
  ['R2', 'R1', 'R6'],
  ['R3', 'R4', 'R5'],
];

function buildAdjacency(): Record<PositionId, PositionId[]> {
  const adj: Partial<Record<PositionId, Set<PositionId>>> = {};

  const addGridAdjacency = (grid: PositionId[][]) => {
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        const pos = grid[r][c];
        if (!adj[pos]) adj[pos] = new Set();
        // 8-directional neighbors
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            if (dr === 0 && dc === 0) continue;
            const nr = r + dr;
            const nc = c + dc;
            if (nr >= 0 && nr < 3 && nc >= 0 && nc < 3) {
              const neighbor = grid[nr][nc];
              adj[pos]!.add(neighbor);
            }
          }
        }
      }
    }
  };

  addGridAdjacency(LEFT_GRID);
  addGridAdjacency(RIGHT_GRID);

  const result: Partial<Record<PositionId, PositionId[]>> = {};
  for (const [key, set] of Object.entries(adj)) {
    result[key as PositionId] = Array.from(set as Set<PositionId>);
  }
  return result as Record<PositionId, PositionId[]>;
}

export const BOARD_ADJACENCY: Record<PositionId, PositionId[]> = buildAdjacency();

// ─── Board Layout for Rendering ──────────────────────────────────────────────

export interface PositionLayout {
  id: PositionId;
  x: number; // percentage 0-100
  y: number; // percentage 0-100
  side: 'left' | 'right' | 'shared';
  label: string;
}

export const BOARD_LAYOUT: PositionLayout[] = [
  // Left grid — center at ~25%, right edge at ~46%
  // Row 0: L5, L4, L3
  { id: 'L5', x: 5,  y: 10, side: 'left',   label: 'L5' },
  { id: 'L4', x: 18, y: 10, side: 'left',   label: 'L4' },
  { id: 'L3', x: 31, y: 10, side: 'left',   label: 'L3' },
  // Row 1: L6, L1, L2
  { id: 'L6', x: 5,  y: 45, side: 'left',   label: 'L6' },
  { id: 'L1', x: 18, y: 45, side: 'left',   label: 'L1' },
  { id: 'L2', x: 31, y: 45, side: 'left',   label: 'L2' },
  // Row 2: L7, L8, S9
  { id: 'L7', x: 5,  y: 80, side: 'left',   label: 'L7' },
  { id: 'L8', x: 18, y: 80, side: 'left',   label: 'L8' },
  // Shared S9 — at intersection
  { id: 'S9', x: 50, y: 80, side: 'shared', label: '9' },
  // Right grid — S9 at top-left (~54%), right edge at ~95%
  // Row 0: S9(already), R8, R7
  { id: 'R8', x: 68, y: 10, side: 'right',  label: 'R8' },
  { id: 'R7', x: 82, y: 10, side: 'right',  label: 'R7' },
  // Row 1: R2, R1, R6
  { id: 'R2', x: 50, y: 45, side: 'right',  label: 'R2' },
  { id: 'R1', x: 68, y: 45, side: 'right',  label: 'R1' },
  { id: 'R6', x: 82, y: 45, side: 'right',  label: 'R6' },
  // Row 2: R3, R4, R5
  { id: 'R3', x: 50, y: 80, side: 'right',  label: 'R3' },
  { id: 'R4', x: 68, y: 80, side: 'right',  label: 'R4' },
  { id: 'R5', x: 82, y: 80, side: 'right',  label: 'R5' },
];

// ─── Board Initialization ────────────────────────────────────────────────────

export function createInitialBoard(): Record<PositionId, BoardPosition> {
  const board: Partial<Record<PositionId, BoardPosition>> = {};

  const leftGridMap: Record<string, { row: number; col: number }> = {
    L5: { row: 0, col: 0 }, L4: { row: 0, col: 1 }, L3: { row: 0, col: 2 },
    L6: { row: 1, col: 0 }, L1: { row: 1, col: 1 }, L2: { row: 1, col: 2 },
    L7: { row: 2, col: 0 }, L8: { row: 2, col: 1 }, S9: { row: 2, col: 2 },
  };

  const rightGridMap: Record<string, { row: number; col: number }> = {
    S9: { row: 0, col: 0 }, R8: { row: 0, col: 1 }, R7: { row: 0, col: 2 },
    R2: { row: 1, col: 0 }, R1: { row: 1, col: 1 }, R6: { row: 1, col: 2 },
    R3: { row: 2, col: 0 }, R4: { row: 2, col: 1 }, R5: { row: 2, col: 2 },
  };

  const allPositions: PositionId[] = [
    'L1','L2','L3','L4','L5','L6','L7','L8',
    'S9',
    'R1','R2','R3','R4','R5','R6','R7','R8',
  ];

  for (const id of allPositions) {
    const inLeft = id in leftGridMap;
    const inRight = id in rightGridMap;
    const isShared = id === 'S9';
    const gridPos = inLeft ? leftGridMap[id] : rightGridMap[id];

    board[id] = {
      id,
      occupiedBy: null,
      isSpot9: isShared,
      gridLeft: inLeft,
      gridRight: inRight,
      displayLabel: isShared ? '9' : id,
      gridRow: gridPos.row,
      gridCol: gridPos.col,
      side: isShared ? 'shared' : inLeft ? 'left' : 'right',
    };
  }

  return board as Record<PositionId, BoardPosition>;
}

// ─── Piece Distribution ──────────────────────────────────────────────────────

export function getPieceDistribution(playerCount: number): number[] {
  switch (playerCount) {
    case 2: return [9, 8];
    case 3: return [6, 6, 5];
    case 4: return [5, 4, 4, 4];
    case 5: return [4, 4, 3, 3, 3];
    default: return [9, 8];
  }
}

// ─── Player Colors ───────────────────────────────────────────────────────────

export const PLAYER_COLORS = [
  { color: '#7C3AED', colorVar: 'player-1', name: 'Violet' },
  { color: '#059669', colorVar: 'player-2', name: 'Emerald' },
  { color: '#DC2626', colorVar: 'player-3', name: 'Crimson' },
  { color: '#D97706', colorVar: 'player-4', name: 'Amber' },
  { color: '#0EA5E9', colorVar: 'player-5', name: 'Sky' },
];

// ─── Formation Detection ─────────────────────────────────────────────────────

/**
 * Formation definitions based on the ViNCi grid layout.
 * Lines: 3 collinear positions in a row, column, or diagonal within a grid.
 * Triangles: 3 positions forming a triangle (L-shape or diagonal cluster).
 * Squares: 4 positions forming a 2x2 square within a grid.
 */

// All possible lines (rows, cols, diagonals) in left grid
const LEFT_LINES: PositionId[][] = [
  // Rows
  ['L5', 'L4', 'L3'],
  ['L6', 'L1', 'L2'],
  ['L7', 'L8', 'S9'],
  // Cols
  ['L5', 'L6', 'L7'],
  ['L4', 'L1', 'L8'],
  ['L3', 'L2', 'S9'],
  // Diagonals
  ['L5', 'L1', 'S9'],
  ['L3', 'L1', 'L7'],
];

// All possible lines in right grid
const RIGHT_LINES: PositionId[][] = [
  // Rows
  ['S9', 'R8', 'R7'],
  ['R2', 'R1', 'R6'],
  ['R3', 'R4', 'R5'],
  // Cols
  ['S9', 'R2', 'R3'],
  ['R8', 'R1', 'R4'],
  ['R7', 'R6', 'R5'],
  // Diagonals
  ['S9', 'R1', 'R5'],
  ['R7', 'R1', 'R3'],
];

// All possible 2x2 squares in left grid
const LEFT_SQUARES: PositionId[][] = [
  ['L5', 'L4', 'L6', 'L1'],
  ['L4', 'L3', 'L1', 'L2'],
  ['L6', 'L1', 'L7', 'L8'],
  ['L1', 'L2', 'L8', 'S9'],
];

// All possible 2x2 squares in right grid
const RIGHT_SQUARES: PositionId[][] = [
  ['S9', 'R8', 'R2', 'R1'],
  ['R8', 'R7', 'R1', 'R6'],
  ['R2', 'R1', 'R3', 'R4'],
  ['R1', 'R6', 'R4', 'R5'],
];

// Triangles: any 3 mutually adjacent positions (not forming a line)
// We detect triangles as any 3 positions where all are adjacent to each other
// and they don't form a straight line
function getTriangles(grid: PositionId[][]): PositionId[][] {
  const triangles: PositionId[][] = [];
  const positions = grid.flat();

  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      for (let k = j + 1; k < positions.length; k++) {
        const a = positions[i];
        const b = positions[j];
        let c = positions[k];

        const abAdj = BOARD_ADJACENCY[a]?.includes(b);
        const bcAdj = BOARD_ADJACENCY[b]?.includes(c);
        const acAdj = BOARD_ADJACENCY[a]?.includes(c);

        if (abAdj && bcAdj && acAdj) {
          // Check it's not a straight line (already covered by LINE detection)
          const isLine = [...LEFT_LINES, ...RIGHT_LINES].some(
            (line) =>
              line.includes(a) && line.includes(b) && line.includes(c)
          );
          if (!isLine) {
            triangles.push([a, b, c]);
          }
        }
      }
    }
  }

  return triangles;
}

const LEFT_TRIANGLES = getTriangles(LEFT_GRID);
const RIGHT_TRIANGLES = getTriangles(RIGHT_GRID);

const FORMATION_POINTS: Record<FormationType, number> = {
  triangle: 1,
  line: 3,
  square: 2,
};

function formationKey(positions: PositionId[]): string {
  return [...positions].sort().join('-');
}

export function detectFormations(
  board: Record<PositionId, BoardPosition>,
  playerId: string
): DetectedFormation[] {
  const formations: DetectedFormation[] = [];
  const seenKeys = new Set<string>();

  const playerPositions = new Set(
    Object.values(board)
      .filter((p) => p.occupiedBy === playerId)
      .map((p) => p.id)
  );

  const checkFormation = (
    positions: PositionId[],
    type: FormationType,
    turnDetected: number
  ) => {
    if (positions.every((p) => playerPositions.has(p))) {
      const key = formationKey(positions);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        formations.push({
          id: `${type}-${key}`,
          type,
          positions,
          playerId,
          points: FORMATION_POINTS[type],
          turnDetected,
        });
      }
    }
  };

  // Lines
  [...LEFT_LINES, ...RIGHT_LINES].forEach((line) =>
    checkFormation(line, 'line', 0)
  );

  // Squares
  [...LEFT_SQUARES, ...RIGHT_SQUARES].forEach((sq) =>
    checkFormation(sq, 'square', 0)
  );

  // Triangles
  [...LEFT_TRIANGLES, ...RIGHT_TRIANGLES].forEach((tri) =>
    checkFormation(tri, 'triangle', 0)
  );

  return formations;
}

export function detectNewFormations(
  board: Record<PositionId, BoardPosition>,
  playerId: string,
  existingFormationIds: Set<string>,
  turnNumber: number
): DetectedFormation[] {
  const all = detectFormations(board, playerId);
  return all
    .filter((f) => !existingFormationIds.has(f.id))
    .map((f) => ({ ...f, turnDetected: turnNumber }));
}

export function calculateScore(formations: DetectedFormation[]): number {
  return formations.reduce((sum, f) => sum + f.points, 0);
}

// ─── Move Validation ─────────────────────────────────────────────────────────

export function isValidMove(
  board: Record<PositionId, BoardPosition>,
  position: PositionId,
  playerId: string,
  rules: GameRules
): { valid: boolean; reason?: string } {
  const pos = board[position];

  if (!pos) return { valid: false, reason: 'Invalid position' };
  if (pos.occupiedBy !== null) {
    return { valid: false, reason: 'Position already occupied' };
  }

  // Sweetwater Rule: if enabled, players cannot place on Spot 9 unless it's their last piece
  // (simplified: Spot 9 is always available unless Sweetwater restricts it)
  // For this implementation, Spot 9 is available to any player (first come, first served)

  return { valid: true };
}

// ─── Game State Transitions ───────────────────────────────────────────────────

export function applyMove(
  state: GameState,
  position: PositionId
): GameState {
  const player = state.players[state.currentPlayerIndex];
  if (!player || player.piecesRemaining <= 0) return state;

  const validation = isValidMove(state.board, position, player.id, state.rules);
  if (!validation.valid) return state;

  // Place piece
  const newBoard = {
    ...state.board,
    [position]: { ...state.board[position], occupiedBy: player.id },
  };

  // Detect new formations for this player
  const existingIds = new Set(
    state.allFormations
      .filter((f) => f.playerId === player.id)
      .map((f) => f.id)
  );
  const newFormations = detectNewFormations(
    newBoard,
    player.id,
    existingIds,
    state.turnNumber
  );

  const pointsEarned = newFormations.reduce((s, f) => s + f.points, 0);

  // Update player
  const updatedPlayer: Player = {
    ...player,
    piecesRemaining: player.piecesRemaining - 1,
    score: player.score + pointsEarned,
    formations: [...player.formations, ...newFormations],
  };

  const updatedPlayers = state.players.map((p) =>
    p.id === player.id ? updatedPlayer : p
  );

  // Move record
  const moveRecord: MoveRecord = {
    id: `move-${state.turnNumber}-${player.id}`,
    turnNumber: state.turnNumber,
    playerId: player.id,
    playerName: player.name,
    position,
    formationsCreated: newFormations,
    pointsEarned,
    timestamp: Date.now(),
  };

  // Check if all pieces placed
  const allPiecesPlaced = updatedPlayers.every((p) => p.piecesRemaining === 0);

  // Find next player with pieces remaining
  let nextIndex = (state.currentPlayerIndex + 1) % state.players.length;
  let attempts = 0;
  while (
    updatedPlayers[nextIndex].piecesRemaining === 0 &&
    !allPiecesPlaced &&
    attempts < state.players.length
  ) {
    nextIndex = (nextIndex + 1) % state.players.length;
    attempts++;
  }

  const newPhase: GamePhase =
    allPiecesPlaced
      ? state.rules.balancingPhase
        ? 'balancing' :'gameover' :'playing';

  return {
    ...state,
    board: newBoard,
    players: updatedPlayers,
    currentPlayerIndex: allPiecesPlaced ? 0 : nextIndex,
    allFormations: [...state.allFormations, ...newFormations],
    moveHistory: [...state.moveHistory, moveRecord],
    turnNumber: state.turnNumber + 1,
    phase: newPhase,
    lastMoveFormations: newFormations,
    balancingState:
      newPhase === 'balancing'
        ? initBalancingState(updatedPlayers)
        : state.balancingState,
  };
}

// ─── Balancing Phase ──────────────────────────────────────────────────────────

function initBalancingState(players: Player[]): BalancingState {
  // Player 1 protects one piece, Player 2 removes one unprotected piece from Player 1
  // Continue per player order
  const actions: BalancingAction[] = [];
  for (let i = 0; i < players.length; i++) {
    actions.push({ type: 'protect', playerId: players[i].id });
    if (i + 1 < players.length) {
      actions.push({
        type: 'remove',
        playerId: players[i + 1].id,
        targetPlayerId: players[i].id,
      });
    }
  }
  return {
    currentActionIndex: 0,
    actions,
    protectedPositions: [],
    phase: 'protecting',
  };
}

export function applyBalancingProtect(
  state: GameState,
  position: PositionId
): GameState {
  if (!state.balancingState) return state;
  const bs = state.balancingState;
  const currentAction = bs.actions[bs.currentActionIndex];
  if (!currentAction || currentAction.type !== 'protect') return state;

  const newBs: BalancingState = {
    ...bs,
    protectedPositions: [...bs.protectedPositions, position],
    currentActionIndex: bs.currentActionIndex + 1,
    phase: 'removing',
  };

  return { ...state, balancingState: newBs };
}

export function applyBalancingRemove(
  state: GameState,
  position: PositionId
): GameState {
  if (!state.balancingState) return state;
  const bs = state.balancingState;
  const currentAction = bs.actions[bs.currentActionIndex];
  if (!currentAction || currentAction.type !== 'remove') return state;

  // Cannot remove protected position
  if (bs.protectedPositions.includes(position)) return state;

  const targetPlayerId = currentAction.targetPlayerId;
  if (state.board[position].occupiedBy !== targetPlayerId) return state;

  // Remove piece from board
  const newBoard = {
    ...state.board,
    [position]: { ...state.board[position], occupiedBy: null },
  };

  // Recalculate formations for affected player
  const targetPlayer = state.players.find((p) => p.id === targetPlayerId);
  if (!targetPlayer) return state;

  const newFormations = detectFormations(newBoard, targetPlayerId);
  const newScore = calculateScore(newFormations);

  const updatedPlayers = state.players.map((p) =>
    p.id === targetPlayerId
      ? { ...p, formations: newFormations, score: newScore }
      : p
  );

  let nextIndex = bs.currentActionIndex + 1;
  const isComplete = nextIndex >= bs.actions.length;

  const newBs: BalancingState = {
    ...bs,
    currentActionIndex: nextIndex,
    phase: isComplete ? 'complete' : 'protecting',
  };

  // Recalculate all formations
  const allFormations = updatedPlayers.flatMap((p) =>
    detectFormations(newBoard, p.id)
  );

  return {
    ...state,
    board: newBoard,
    players: updatedPlayers,
    allFormations,
    balancingState: newBs,
    phase: isComplete ? 'gameover' : 'balancing',
  };
}

// ─── Winner Calculation ───────────────────────────────────────────────────────

export function determineWinner(players: Player[], rules: GameRules): Player | null {
  if (players.length === 0) return null;

  const sorted = [...players].sort((a, b) => b.score - a.score);
  const topScore = sorted[0].score;
  const tied = sorted.filter((p) => p.score === topScore);

  if (tied.length === 1) return tied[0];

  // Texas Tie Break: most formations wins
  if (rules.texasTieBreak) {
    const maxFormations = Math.max(...tied.map((p) => p.formations.length));
    const byFormations = tied.filter((p) => p.formations.length === maxFormations);
    if (byFormations.length === 1) return byFormations[0];
  }

  // Still tied — return first (could be extended)
  return tied[0];
}

// ─── AI Engine ────────────────────────────────────────────────────────────────

export function getAIMove(
  state: GameState,
  playerId: string,
  difficulty: AIDifficulty
): PositionId | null {
  const player = state.players.find((p) => p.id === playerId);
  if (!player || player.piecesRemaining <= 0) return null;

  const emptyPositions = Object.values(state.board)
    .filter((p) => p.occupiedBy === null)
    .map((p) => p.id);

  if (emptyPositions.length === 0) return null;

  if (difficulty === 'easy') {
    // Random valid move
    const idx = Math.floor(Math.random() * emptyPositions.length);
    return emptyPositions[idx];
  }

  // Score each possible move
  const scoredMoves = emptyPositions.map((pos) => {
    const testBoard = {
      ...state.board,
      [pos]: { ...state.board[pos], occupiedBy: playerId },
    };
    const existingIds = new Set(player.formations.map((f) => f.id));
    const newForms = detectNewFormations(testBoard, playerId, existingIds, state.turnNumber);
    const points = newForms.reduce((s, f) => s + f.points, 0);

    // Bonus for Spot 9
    const spot9Bonus = pos === 'S9' ? 0.5 : 0;

    // Block opponent score (medium/hard)
    let blockBonus = 0;
    if (difficulty === 'medium' || difficulty === 'hard') {
      for (const opp of state.players) {
        if (opp.id === playerId) continue;
        const oppExistingIds = new Set(opp.formations.map((f) => f.id));
        const oppForms = detectNewFormations(testBoard, opp.id, oppExistingIds, state.turnNumber);
        blockBonus += oppForms.reduce((s, f) => s + f.points, 0) * 0.7;
      }
    }

    // Hard: look ahead one more level
    let lookaheadBonus = 0;
    if (difficulty === 'hard') {
      const adjacentEmpty = BOARD_ADJACENCY[pos]?.filter(
        (adj) => testBoard[adj]?.occupiedBy === null
      ) ?? [];
      for (const adjPos of adjacentEmpty.slice(0, 3)) {
        const testBoard2 = {
          ...testBoard,
          [adjPos]: { ...testBoard[adjPos], occupiedBy: playerId },
        };
        const existingIds2 = new Set([
          ...existingIds,
          ...newForms.map((f) => f.id),
        ]);
        const forms2 = detectNewFormations(testBoard2, playerId, existingIds2, state.turnNumber + 1);
        lookaheadBonus += forms2.reduce((s, f) => s + f.points, 0) * 0.4;
      }
    }

    return {
      pos,
      score: points + spot9Bonus + blockBonus + lookaheadBonus,
    };
  });

  scoredMoves.sort((a, b) => b.score - a.score);

  // Medium: slight randomness
  if (difficulty === 'medium') {
    const topN = scoredMoves.slice(0, 3);
    const idx = Math.floor(Math.random() * topN.length);
    return topN[idx].pos;
  }

  return scoredMoves[0].pos;
}

// ─── Game Factory ─────────────────────────────────────────────────────────────

export function createNewGame(
  playerConfigs: Array<{
    name: string;
    isAI: boolean;
    aiDifficulty?: AIDifficulty;
    color: string;
    colorVar: string;
  }>,
  rules: GameRules
): GameState {
  const distribution = getPieceDistribution(playerConfigs.length);

  const players: Player[] = playerConfigs.map((cfg, i) => ({
    id: `player-${i + 1}`,
    name: cfg.name,
    color: cfg.color,
    colorVar: cfg.colorVar,
    isAI: cfg.isAI,
    aiDifficulty: cfg.aiDifficulty,
    piecesRemaining: distribution[i],
    piecesTotal: distribution[i],
    score: 0,
    formations: [],
  }));

  return {
    id: `game-${Date.now()}`,
    phase: 'playing',
    players,
    currentPlayerIndex: 0,
    board: createInitialBoard(),
    allFormations: [],
    moveHistory: [],
    turnNumber: 1,
    rules,
    winner: null,
  };
}